import React, { useState, useEffect } from "react";
import {
  Drawer,
  IconButton,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Grid,
  Typography,
  AppBar,
  Toolbar,
} from "@mui/material";
import {
  DragHandle,
  Delete,
  Add,
  Edit,
  Save,
  Close,
} from "@mui/icons-material";
import { DragDropContext as RawDragDropContext, Droppable as RawDroppable, Draggable as RawDraggable } from "react-beautiful-dnd";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import moment from "moment";
import { env } from "../../../../env";
import useUpdateAssetData from "../../Hooks/updateAssetsHook";
import getKpiChangeSummary from "../../utils/getKpiChangeSummary";
import { RenderChangeSummary } from "../../utils/renderComponentSummary";
import { useSelector } from "react-redux";
import { sortMapArray } from "../../utils/sortArrayObjectByKey";

const DragDropContext = RawDragDropContext as unknown as React.ComponentType<any>;
const Droppable = RawDroppable as unknown as React.ComponentType<any>;
const Draggable = RawDraggable as unknown as React.ComponentType<any>;
const baseURL = env.microservice_assetsmgt;
// KPI Type
interface Kpi {
  kpiName: string;
  kpitag: string | string[];
  kpiUnit: string;
}

interface KpiChange {
  modifiedBy: string;
  modifiedDate: string;
  modifiedChanges: any;
}

interface KpiSidebarProps {
  open: boolean;
  onClose: () => void;
  defaultKpis: Kpi[];
  kpiChangeSummary: KpiChange[];
  onKpisUpdate: (updatedKpis: Kpi[]) => void;
  assetId: string;
  setUpdatedKpiList?: any;
  updatedKpiList?: any;
  setQueryKey?: any;
  subCategoryData?: { subCategoryAsset: string; kpiDataPoints: Kpi[] }[];
  enableAreaGrouping?: boolean;
}

const KpiSidebar: React.FC<KpiSidebarProps> = ({
  open,
  onClose,
  defaultKpis,
  kpiChangeSummary,
  onKpisUpdate,
  assetId,
  setUpdatedKpiList = () => {},
  updatedKpiList = [],
  setQueryKey = () => {},
  subCategoryData,
  enableAreaGrouping = false,
}) => {
  const [kpis, setKpis] = useState<Kpi[]>(defaultKpis);
  const [okpis, setOKpis] = useState<Kpi[]>(defaultKpis);
  const [kpiChangeHistory, setKpiChangeHistory] =
    useState<KpiChange[]>(kpiChangeSummary);
  const [newKpi, setNewKpi] = useState<Kpi>({
    kpiName: "",
    kpitag: "",
    kpiUnit: "",
  });
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [isModified, setIsModified] = useState(false);
  const [queryRefreshKey, setQueryRefreshKey] = useState<string>("");
  const [errorForm, setErrorForm] = useState<string>("");
  const [historyView, setHistoryView] = useState<boolean>(false);
  const usersList = useSelector((state: any) => state.usersList);

  useEffect(() => {
    setKpis(defaultKpis);
    setOKpis(defaultKpis);
  }, [defaultKpis]);

  const {
    data: updated_asset_kpi_records,
    error,
    isLoading,
  } = useUpdateAssetData(
    `${baseURL}assets/${assetId}`,
    queryRefreshKey,
    undefined, // no queryParams anymore
    [assetId], // enable only when assetId is available
    { kpiDataPoints: kpis, kpiChangeSummary: kpiChangeHistory } // your data goes here
  );
  // Handle Adding New KPI
  const handleAddKpi = () => {
    if (newKpi.kpiName && newKpi.kpitag && newKpi.kpiUnit) {
      if (kpis.some((kpi) => kpi.kpiName === newKpi.kpiName)) return; // Prevent duplicates
      const updatedKpis = [...kpis, newKpi];
      setKpis(updatedKpis);
      setNewKpi({ kpiName: "", kpitag: "", kpiUnit: "" });
      setIsModified(true);
    }
  };

  // Handle Drag & Drop Reordering
  const onDragEnd = (result: any) => {
    if (!result.destination) return;
    const reorderedKpis = [...kpis];
    const [movedItem] = reorderedKpis.splice(result.source.index, 1);
    reorderedKpis.splice(result.destination.index, 0, movedItem);
    setKpis(reorderedKpis);
    setIsModified(true);
  };

  // Handle KPI Removal
  const removeKpi = (index: number) => {
    const updatedKpis = kpis.filter((_, i) => i !== index);
    setKpis(updatedKpis);
    setIsModified(true);
  };

  // Handle Editing KPI
  const handleEditKpi = (index: number) => {
    setEditIndex(index);
    setNewKpi(kpis[index]);
  };

  // Handle Save KPI Changes
  const handleSaveKpi = () => {
    if (editIndex !== null) {
      const updatedKpis = [...kpis];
      updatedKpis[editIndex] = newKpi;
      setKpis(updatedKpis);
      setEditIndex(null);
      setNewKpi({ kpiName: "", kpitag: "", kpiUnit: "" });
      setIsModified(true);
    }
  };

  // Handle Final Save (Send Data to Parent)
  const handleFinalSave = () => {
    const changeSummary = getKpiChangeSummary(kpis, okpis);

    const latestChange: KpiChange = {
      modifiedBy: usersList?.currentUser.user_ad_name, // you can pass a real user
      modifiedDate: moment().format("YYYY-MM-DD HH:mm:ss"),
      modifiedChanges: changeSummary,
    };

    const updatedSummary = [...kpiChangeHistory, latestChange];
    setKpiChangeHistory(updatedSummary);
    onKpisUpdate(kpis);
    setIsModified(false);
    setQueryRefreshKey(
      `asset_kpi_data_update_${assetId}_${moment().valueOf()}`
    );
    setUpdatedKpiList([]);
  };

  const handleAddOrUpdateKpi = () => {
    if (!newKpi.kpiName || !newKpi.kpitag) {
      setErrorForm("KPI Name and KPI Tag are required.");
      return;
    }

    const isDuplicate: boolean = kpis.some((kpi, index) => {
      const currentTags = Array.isArray(kpi.kpitag)
        ? kpi.kpitag.map((tag) => tag.trim().toLowerCase())
        : [kpi.kpitag.trim().toLowerCase()];

      const newTags = Array.isArray(newKpi.kpitag)
        ? newKpi.kpitag.map((tag) => tag.trim().toLowerCase())
        : [newKpi.kpitag.trim().toLowerCase()];

      return (
        index !== editIndex &&
        (kpi.kpiName.trim().toLowerCase() ===
          newKpi.kpiName.trim().toLowerCase() ||
          currentTags.some((tag) => newTags.includes(tag)))
      );
    });

    if (isDuplicate) {
      setErrorForm("KPI Name and KPI Tag must be unique.");
      return;
    }

    setErrorForm("");

    if (editIndex !== null) {
      const updatedKpis = [...kpis];
      updatedKpis[editIndex] = newKpi;
      setKpis(updatedKpis);
      setEditIndex(null);
    } else {
      setKpis([...kpis, newKpi]);
    }

    setNewKpi({ kpiName: "", kpitag: "", kpiUnit: "" });
    setIsModified(true);
  };

  useEffect(() => {
    if (
      JSON.stringify(updatedKpiList) !=
        JSON.stringify(updated_asset_kpi_records?.data?.data?.kpiDataPoints) &&
      !updatedKpiList.length &&
      updated_asset_kpi_records &&
      updated_asset_kpi_records?.data?.data?.kpiDataPoints
    ) {
      setUpdatedKpiList(updated_asset_kpi_records?.data?.data?.kpiDataPoints);
      setQueryKey(`post_kpi_update_${moment().valueOf()}`);
    }
  }, [updated_asset_kpi_records]);

  useEffect(() => {
    setKpiChangeHistory(kpiChangeSummary);
  }, [kpiChangeSummary]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ width: 400, height: "100%", backgroundImage: "none" }}
    >
      {/* Header */}
      <AppBar
        position="static"
        sx={{
          padding: 2,
          marginTop: "60px",
          backgroundImage: "none",
          backgroundColor: "rgb(39 48 96)",
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6">Manage KPIs</Typography>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setHistoryView(!historyView)}
              sx={{ marginRight: 1 }}
            >
              {historyView ? "View KPI list" : "View History"}
            </Button>
            <IconButton edge="end" color="inherit" onClick={onClose}>
              <Close />
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>

      {historyView ? (
        <div
          style={{
            width: 600,
            padding: 8,
            backgroundColor: "rgb(46 57 118)",
            backgroundImage: "none",
            minHeight: "calc(100vh - 130px)",
          }}
        >
          {kpiChangeHistory && kpiChangeHistory.length > 0 ? (
            sortMapArray(kpiChangeHistory)
              .sortByKey("modifiedDate", "desc")
              .map((entry) => (
                <ListItem
                  key={entry.modifiedDate}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 1,
                    backgroundColor: "#232957",
                    borderRadius: "5px",
                    mb: 1,
                  }}
                >
                  <ListItemText
                    primary={
                      <>
                        <Typography component="span" fontWeight="bold">
                          Modified By
                        </Typography>{" "}
                        -- {entry.modifiedBy} {"  "}
                        <Typography component="span" fontWeight="bold">
                          Modified On
                        </Typography>{" "}
                        -- {entry.modifiedDate}
                      </>
                    }
                    secondary={
                      <>
                        <RenderChangeSummary changes={entry.modifiedChanges} />
                      </>
                    }
                  />
                </ListItem>
              ))
          ) : (
            <div
              style={{
                minHeight: "calc(100vh - 180px)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography color="textSecondary" align="center">
                No change history available.
              </Typography>
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            width: 600,
            padding: 8,
            backgroundColor: "rgb(46 57 118)",
            backgroundImage: "none",
            minHeight: "calc(100vh - 130px)",
          }}
        >
          {/* KPI Input Fields */}
          <Grid container spacing={2} padding={1}>
            <Grid item xs={9}>
              <TextField
                label="KPI kpiName"
                fullWidth
                value={newKpi.kpiName}
                onChange={(e) =>
                  setNewKpi({ ...newKpi, kpiName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Unit"
                fullWidth
                value={newKpi.kpiUnit}
                onChange={(e) =>
                  setNewKpi({ ...newKpi, kpiUnit: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="KPI kpitag"
                fullWidth
                value={newKpi.kpitag}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewKpi({
                    ...newKpi,
                    kpitag: value.includes(",")
                      ? value.split(",").map((tag) => tag.trim())
                      : value,
                  });
                }}
              />
            </Grid>
          </Grid>
          <Grid spacing={1} padding={1}>
            {errorForm && <Typography color="error">{errorForm}</Typography>}
          </Grid>
          <Grid container justifyContent="flex-end" spacing={1.5} padding={1}>
            <Grid item>
              <Button
                startIcon={editIndex !== null ? <Save /> : <Add />}
                variant="contained"
                color="primary"
                onClick={handleAddOrUpdateKpi}
              >
                {editIndex !== null ? "Save Changes" : "Add KPI"}
              </Button>
            </Grid>
          </Grid>

          {/* KPI List with Drag & Drop */}
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="kpiList">
              {(provided: any) => (
                <List
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{ mt: 2 }}
                >
                  {kpis.map((kpiObj: any, index: any) => {
                    const kpi = Object.keys(kpiObj).includes("kpi")
                      ? kpiObj?.kpi
                      : kpiObj;
                    return (
                      <Draggable
                        key={kpi.kpiName}
                        draggableId={kpi.kpiName}
                        index={index}
                      >
                        {(provided: any) => (
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              py: 1,
                              backgroundColor: "#232957",
                              borderRadius: "5px",
                              mb: 1,
                            }}
                          >
                            <DragHandle
                              sx={{ cursor: "grab", color: "gray" }}
                            />
                            <ListItemText
                              primary={`${kpi.kpiName} | ${kpi.kpiUnit}`}
                              secondary={
                                <Typography variant="body2" component="span">
                                  {Array.isArray(kpi.kpitag)
                                    ? kpi.kpitag.map(
                                        (tag: string, index: any) => (
                                          <React.Fragment key={index}>
                                            {tag.trim()}
                                            <br />
                                          </React.Fragment>
                                        )
                                      )
                                    : kpi.kpitag}
                                </Typography>
                              }
                            />
                            <IconButton
                              onClick={() => handleEditKpi(index)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              onClick={() => removeKpi(index)}
                              edge="end"
                              color="error"
                            >
                              <HighlightOffOutlinedIcon />
                            </IconButton>
                          </ListItem>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>

          <Grid container justifyContent="flex-end" spacing={1.5} padding={1}>
            <Grid item>
              {/* Save Button */}
              <Button
                startIcon={<Save />}
                variant="contained"
                color="primary"
                sx={{ mt: 1, width: "250px" }}
                onClick={handleFinalSave}
                disabled={!isModified}
              >
                Save All Changes
              </Button>
            </Grid>
          </Grid>
        </div>
      )}
    </Drawer>
  );
};

export default KpiSidebar;
